import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  getFormations,
  createFormation,
  updateFormation,
  deleteFormation,
  analyserImportFormations,
  analyserImportFormationsSite,
  supprimerFormations,
  supprimerToutesFormations,
  texteCatalogueSite,
  enregistrerImportFormations,
} from '../services/formationService';
import SelectRecherche from '../components/SelectRecherche';
import ImportTexteModal from '../components/ImportTexteModal';
import ConfirmationSuppression from '../components/ConfirmationSuppression';
import FiltresRepliables from '../components/FiltresRepliables';
import '../assets/styles/Parametrage.css';
import '../assets/styles/TexteBorne.css';
import BarreProgression, { useProgression } from '../components/BarreProgression';
import RemplirParTexte from '../components/RemplirParTexte';

/** Familles du catalogue (colonne formations.categorie, migration 010). */
const CATEGORIES = [
  { value: 'economique', label: 'Économique' },
  { value: 'ssct', label: 'SSCT' },
  { value: 'referents', label: 'Référents du CSE' },
  { value: 'cse_plus', label: 'Perfectionnement CSE Plus+' },
];

/** Tranches d'effectif du catalogue : une formation sans borne vaut pour toutes. */
const TAILLES = [
  { value: '11-49', label: '11 à 49', min: 11, max: 49 },
  { value: '50-299', label: '50 à 299', min: 50, max: 299 },
  { value: '300', label: '+300', min: 300, max: Infinity },
];

function tailleCorrespond(f, valeur) {
  if (!valeur) return true;
  const t = TAILLES.find((x) => x.value === valeur);
  if (!t) return true;
  const min = Number(f.effectifMin) || 0;
  const max = f.effectifMax === null || f.effectifMax === undefined || f.effectifMax === '' ? Infinity : Number(f.effectifMax);
  return min <= t.max && max >= t.min;
}

/** Qui paie la formation — l'information figure au catalogue. */
const FINANCEMENTS = [
  { value: 'cse', label: 'Budget du CSE' },
  { value: 'employeur', label: 'À la charge de l’employeur' },
];

/**
 * INTRA = forfait journalier groupe, déduit du tarif INTER par ce coefficient.
 * Doit rester aligné sur COEFFICIENT_INTRA côté serveur : ici il ne sert qu'à
 * afficher le tarif que le backend calculera.
 */
const COEFFICIENT_INTRA = 3;

const FORMATION_VIDE = {
  code: '',
  intitule: '',
  categorie: 'cse_plus',
  dureeJours: '',
  dureeMinJours: '',
  dureeMaxJours: '',
  dureeHeures: '',
  prixJourInterHt: '',
  effectifMin: '',
  effectifMax: '',
  obligatoire: false,
  distancielPossible: true,
  financement: 'cse',
  baseLegale: '',
  lieuParDefaut: 'Paris ou Lille & En ligne',
  description: '',
  objectifPedagogique: '',
  actif: true,
};

function formationToForm(formation) {
  return {
    code: formation.code ?? '',
    intitule: formation.intitule ?? '',
    categorie: formation.categorie ?? 'cse_plus',
    dureeJours: formation.dureeJours ?? '',
    dureeMinJours: formation.dureeMinJours ?? '',
    dureeMaxJours: formation.dureeMaxJours ?? '',
    dureeHeures: formation.dureeHeures ?? '',
    prixJourInterHt: formation.prixJourInterHt ?? '',
    effectifMin: formation.effectifMin ?? '',
    effectifMax: formation.effectifMax ?? '',
    obligatoire: formation.obligatoire ?? false,
    distancielPossible: formation.distancielPossible ?? true,
    financement: formation.financement ?? 'cse',
    baseLegale: formation.baseLegale ?? '',
    lieuParDefaut: formation.lieuParDefaut ?? '',
    description: formation.description ?? '',
    objectifPedagogique: formation.objectifPedagogique ?? '',
    actif: formation.actif ?? true,
  };
}

const libelle = (liste, valeur) => liste.find((x) => x.value === valeur)?.label || valeur || '—';

function montant(valeur) {
  const n = Number(valeur);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** « 2 j » ou « 2 à 3 j », avec les heures quand le catalogue les précise. */
function formatDuree(f) {
  const ref = Number(f.dureeJours);
  const min = f.dureeMinJours == null ? null : Number(f.dureeMinJours);
  const max = f.dureeMaxJours == null ? null : Number(f.dureeMaxJours);

  const jours =
    min != null && max != null && min !== max ? `${min} à ${max} j` : `${ref} j`;

  return f.dureeHeures ? `${jours} · ${f.dureeHeures} h` : jours;
}

/** Tranche d'effectif visée. Deux bornes vides = toutes tailles. */
function formatEffectif(f) {
  const { effectifMin: min, effectifMax: max } = f;
  if (min == null && max == null) return 'Toutes tailles';
  if (max == null) return `${min} salariés et +`;
  if (min == null) return `Jusqu'à ${max}`;
  return `${min} à ${max}`;
}

function ParametrageFormations() {
  const [formations, setFormations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // ?q= : arrivée depuis la recherche globale, la liste s'ouvre filtrée.
  const [params] = useSearchParams();
  const [search, setSearch] = useState(() => params.get('q') || '');
  const [categorie, setCategorie] = useState('');
  const [taille, setTaille] = useState('');
  useEffect(() => {
    const q = params.get('q');
    if (q !== null) setSearch(q);
  }, [params]);

  // null = modale fermée ; { id: null } = création ; { id: 12 } = édition
  const [edition, setEdition] = useState(null);
  const [form, setForm] = useState(FORMATION_VIDE);
  const [saving, setSaving] = useState(false);
  const progressionEnregistrement = useProgression(saving, { estimeeMs: 3000 });
  const [formError, setFormError] = useState(null);

  const [suppression, setSuppression] = useState(null);
  // Sélection par cases : suppression groupée, ou de tout le catalogue.
  const [selection, setSelection] = useState(() => new Set());
  const [suppressionGroupee, setSuppressionGroupee] = useState(null); // 'selection' | 'tout'
  const [flash, setFlash] = useState(null);
  // Import par copier-coller d'un catalogue.
  const [importOuvert, setImportOuvert] = useState(false);

  function chargerFormations() {
    setLoading(true);
    setError(null);
    return getFormations()
      .then((res) => setFormations(res.data || []))
      .catch(() => setError('Impossible de charger le catalogue de formations.'))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    chargerFormations();
  }, []);

  function ouvrirCreation() {
    setEdition({ id: null });
    setForm(FORMATION_VIDE);
    setFormError(null);
  }

  function ouvrirEdition(formation) {
    setEdition({ id: formation.id });
    setForm(formationToForm(formation));
    setFormError(null);
  }

  function fermerModale() {
    setEdition(null);
    setFormError(null);
  }

  function handleFieldChange(champ) {
    return (e) => {
      const valeur = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
      setForm((prev) => ({ ...prev, [champ]: valeur }));
    };
  }

  /** Un champ numérique laissé vide vaut null, pas 0. */
  const nombreOuNull = (v) => (v === '' || v === null ? null : Number(v));

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    setFormError(null);

    const payload = {
      ...form,
      dureeJours: Number(form.dureeJours),
      dureeMinJours: nombreOuNull(form.dureeMinJours),
      dureeMaxJours: nombreOuNull(form.dureeMaxJours),
      dureeHeures: nombreOuNull(form.dureeHeures),
      prixJourInterHt: Number(form.prixJourInterHt),
      effectifMin: nombreOuNull(form.effectifMin),
      effectifMax: nombreOuNull(form.effectifMax),
      baseLegale: form.baseLegale || null,
    };

    try {
      if (edition.id === null) {
        await createFormation(payload);
        setFlash('Formation créée.');
      } else {
        await updateFormation(edition.id, payload);
        setFlash('Formation mise à jour.');
      }
      await chargerFormations();
      setEdition(null);
    } catch (err) {
      setFormError(err.message || 'Une erreur est survenue.');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    setSaving(true);
    try {
      await deleteFormation(suppression.id);
      await chargerFormations();
      setFlash('Formation supprimée.');
      setSuppression(null);
    } catch (err) {
      setError(err.message || 'Suppression impossible.');
      setSuppression(null);
    } finally {
      setSaving(false);
    }
  }

  function basculer(id) {
    setSelection((prev) => {
      const suite = new Set(prev);
      if (suite.has(id)) suite.delete(id);
      else suite.add(id);
      return suite;
    });
  }

  async function handleSupprimerGroupe() {
    setSaving(true);
    try {
      const res =
        suppressionGroupee === 'tout'
          ? await supprimerToutesFormations()
          : await supprimerFormations([...selection]);
      const b = res.data || {};
      setFlash(
        `${b.supprimees ?? 0} formation${(b.supprimees ?? 0) > 1 ? 's' : ''} supprimée${(b.supprimees ?? 0) > 1 ? 's' : ''}` +
          (b.sessionsSupprimees ? `, avec ${b.sessionsSupprimees} session${b.sessionsSupprimees > 1 ? 's' : ''} du calendrier` : '') +
          '.'
      );
      setSelection(new Set());
      setSuppressionGroupee(null);
      await chargerFormations();
    } catch (err) {
      setError(err.message || 'Suppression impossible.');
      setSuppressionGroupee(null);
    } finally {
      setSaving(false);
    }
  }

  const filtered = formations.filter((f) => {
    if (categorie && f.categorie !== categorie) return false;
    if (!tailleCorrespond(f, taille)) return false;
    if (!search.trim()) return true;
    const haystack = `${f.code} ${f.intitule}`.toLowerCase();
    return haystack.includes(search.trim().toLowerCase());
  });

  const filtreActif = Boolean(categorie || taille || search.trim());

  // Les comptes des pastilles portent sur tout le catalogue.
  const compteCategorie = (valeur) =>
    formations.filter((f) => (valeur ? f.categorie === valeur : true)).length;
  const compteTaille = (valeur) => formations.filter((f) => tailleCorrespond(f, valeur)).length;

  return (
    <div className="param-page">
      <div className="param-header">
        <div>
          <h1 className="param-title">Formations</h1>
          <p className="param-subtitle">
            Catalogue de référence injecté dans le prompt de génération de devis.
          </p>
        </div>
        <div className="param-header-actions">
          <button
            type="button"
            className="param-btn"
            onClick={() => setImportOuvert('site')}
            title="Lit le catalogue sur echo-cse.fr et propose les fiches à relire"
          >
            Importer depuis le site
          </button>
          <button type="button" className="param-btn param-btn-primary" onClick={ouvrirCreation}>
            + Ajouter une formation
          </button>
        </div>
      </div>

      <FiltresRepliables libelle="Filtres">
        <div className="param-toolbar">
          <input
            type="text"
            className="param-search"
            placeholder="Rechercher un code, un intitulé..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          {filtreActif && (
            <button
              type="button"
              className="param-filtre-clear"
              onClick={() => {
                setSearch('');
                setCategorie('');
                setTaille('');
              }}
            >
              Réinitialiser
            </button>
          )}

          {flash && <span className="param-flash">{flash}</span>}
        </div>

        <div className="param-pastilles-groupe" role="group" aria-label="Catégorie">
          <button
            type="button"
            className={`param-pastille${categorie === '' ? ' param-pastille-active' : ''}`}
            onClick={() => setCategorie('')}
          >
            Toutes <b>{compteCategorie('')}</b>
          </button>
          {CATEGORIES.map((c) => (
            <button
              key={c.value}
              type="button"
              className={`param-pastille${categorie === c.value ? ' param-pastille-active' : ''}`}
              onClick={() => setCategorie(categorie === c.value ? '' : c.value)}
            >
              {c.label} <b>{compteCategorie(c.value)}</b>
            </button>
          ))}
        </div>
        <div className="param-pastilles-groupe" role="group" aria-label="Taille de l’entreprise">
          <span className="param-pastilles-libelle">Taille :</span>
          <button
            type="button"
            className={`param-pastille${taille === '' ? ' param-pastille-active' : ''}`}
            onClick={() => setTaille('')}
          >
            Toutes
          </button>
          {TAILLES.map((t) => (
            <button
              key={t.value}
              type="button"
              className={`param-pastille${taille === t.value ? ' param-pastille-active' : ''}`}
              onClick={() => setTaille(taille === t.value ? '' : t.value)}
              title={`Formations dont la tranche d’effectif recouvre ${t.label} salariés`}
            >
              {t.label} <b>{compteTaille(t.value)}</b>
            </button>
          ))}
        </div>
      </FiltresRepliables>


      <div className="param-card param-card-fill">
        <div className="param-card-header param-card-header-selection">
          <span>
            {formations.length} formation{formations.length > 1 ? 's' : ''} au catalogue
            {filtreActif && ` · ${filtered.length} affichée${filtered.length > 1 ? 's' : ''}`}
          </span>
          <div className="param-selection">
            <button
              type="button"
              className="param-btn param-btn-sm"
              onClick={() =>
                setSelection(
                  selection.size === filtered.length && filtered.length > 0
                    ? new Set()
                    : new Set(filtered.map((f) => f.id))
                )
              }
              disabled={filtered.length === 0}
            >
              {selection.size === filtered.length && filtered.length > 0 ? 'Tout désélectionner' : 'Tout sélectionner'}
            </button>
            <button
              type="button"
              className="param-btn param-btn-sm param-btn-danger"
              onClick={() => setSuppressionGroupee('selection')}
              disabled={selection.size === 0}
            >
              Supprimer la sélection{selection.size ? ` (${selection.size})` : ''}
            </button>
            <button
              type="button"
              className="param-btn param-btn-sm param-btn-danger"
              onClick={() => setSuppressionGroupee('tout')}
              disabled={formations.length === 0}
              title="Vider tout le catalogue, sessions du calendrier comprises"
            >
              Supprimer tout le catalogue
            </button>
          </div>
        </div>

        <div className="param-table-wrap">
          {loading && <div className="param-state">Chargement du catalogue...</div>}
          {!loading && error && <div className="param-state param-state-error">{error}</div>}

          {!loading && !error && filtered.length === 0 && (
            <div className="param-empty">
              <div className="param-empty-title">Aucune formation ne correspond à cette vue.</div>
              <div className="param-empty-subtitle">
                Ajoutez une formation pour enrichir le catalogue.
              </div>
            </div>
          )}

          {!loading && !error && filtered.length > 0 && (
            <div className="param-formations">
              {filtered.map((formation) => (
                <article
                  className={`param-formation${selection.has(formation.id) ? ' param-formation-selectionnee' : ''}`}
                  key={formation.id}
                >
                  <div className="param-formation-tete">
                    <input
                      type="checkbox"
                      className="param-formation-case"
                      checked={selection.has(formation.id)}
                      onChange={() => basculer(formation.id)}
                      aria-label={`Sélectionner ${formation.code}`}
                    />
                    <span className="param-code">{formation.code}</span>
                    <span className="param-categorie">{libelle(CATEGORIES, formation.categorie)}</span>
                    {formation.obligatoire && (
                      <span className="param-tag" title="Formation légalement obligatoire">
                        Obligatoire
                      </span>
                    )}
                  </div>
                  <div className="param-formation-intitule">{formation.intitule}</div>
                  {formation.description && (
                    <div className="param-description texte-borne">{formation.description}</div>
                  )}

                  <div className="param-formation-pied">
                    <div className="param-formation-cellule">
                      <span className="param-formation-libelle">DURÉE</span>
                      <span className="param-nowrap">{formatDuree(formation)}</span>
                    </div>
                    <div className="param-formation-cellule">
                      <span className="param-formation-libelle">EFFECTIF</span>
                      <span>{formatEffectif(formation)}</span>
                    </div>
                    <div className="param-formation-cellule">
                      <span className="param-formation-libelle">TARIF HT</span>
                      {/* Le tarif catalogue est le prix INTER ; l'INTRA en découle. */}
                      <div className="param-tarif">
                        {montant(formation.prixJourInterHt)} € <span>/ pers / j</span>
                      </div>
                      <div className="param-tarif-sub">
                        INTRA {montant(formation.prixJourInterHt * COEFFICIENT_INTRA)} € / j
                        {!formation.distancielPossible && ' · présentiel seul'}
                      </div>
                    </div>
                    <div className="param-formation-cellule">
                      <span className="param-formation-libelle">ÉTAT</span>
                      <span className={`param-pill${formation.actif ? '' : ' param-pill-muted'}`}>
                        {formation.actif ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <div className="param-formation-cellule param-formation-actions">
                      <span className="param-formation-libelle">ACTIONS</span>
                      <div className="param-row-actions">
                        <button
                          type="button"
                          className="param-btn param-btn-sm"
                          onClick={() => ouvrirEdition(formation)}
                        >
                          Modifier
                        </button>
                        <button
                          type="button"
                          className="param-btn param-btn-sm param-btn-danger"
                          onClick={() => setSuppression(formation)}
                        >
                          Supprimer
                        </button>
                      </div>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </div>

      {edition && (
        <div className="param-modal-overlay" onClick={fermerModale}>
          <div className="param-modal" onClick={(e) => e.stopPropagation()}>
            <div className="param-modal-header">
              <h2 className="param-modal-title">
                {edition.id === null ? 'Nouvelle formation' : 'Modifier la formation'}
              </h2>
              <button type="button" className="param-modal-close" onClick={fermerModale}>
                ×
              </button>
            </div>

            <form className="param-form param-modal-body" onSubmit={handleSubmit}>
              {/* En tête de fiche, et collée là pendant le défilement : sous le dernier champ, on ne la voyait pas. */}
              {formError && (
                <div className="param-form-message param-form-message-error">{formError}</div>
              )}

              {/* À la création comme à la modification : le texte collé remplit la fiche. */}
              <RemplirParTexte
                  variante="param"
                  contexte="fiche du catalogue de formations"
                  champs={[
                    { cle: 'code', libelle: 'Code' },
                    { cle: 'intitule', libelle: 'Intitulé' },
                    {
                      cle: 'categorie',
                      libelle: 'Catégorie',
                      type: 'choix',
                      options: CATEGORIES.map((c) => ({ valeur: c.value, libelle: c.label })),
                    },
                    { cle: 'dureeJours', libelle: 'Durée en jours', type: 'nombre' },
                    { cle: 'dureeHeures', libelle: 'Durée en heures', type: 'nombre' },
                    { cle: 'prixJourInterHt', libelle: 'Prix jour INTER HT', type: 'nombre' },
                    { cle: 'effectifMin', libelle: 'Effectif minimum', type: 'nombre' },
                    { cle: 'effectifMax', libelle: 'Effectif maximum', type: 'nombre' },
                    { cle: 'baseLegale', libelle: 'Base légale' },
                    { cle: 'lieuParDefaut', libelle: 'Lieu par défaut' },
                    { cle: 'description', libelle: 'Description' },
                    { cle: 'objectifPedagogique', libelle: 'Objectif pédagogique' },
                  ]}
                  onValeurs={(v) => setForm((prev) => ({ ...prev, ...v }))}
                />

              <div className="param-form-row">
                <label className="param-field">
                  <span className="param-label">Code</span>
                  <input
                    type="text"
                    className="param-input"
                    value={form.code}
                    onChange={handleFieldChange('code')}
                    required
                  />
                </label>

                <label className="param-field param-field-grow">
                  <span className="param-label">Intitulé</span>
                  <input
                    type="text"
                    className="param-input"
                    value={form.intitule}
                    onChange={handleFieldChange('intitule')}
                    required
                  />
                </label>

                <label className="param-field">
                  <span className="param-label">Catégorie</span>
                  <SelectRecherche
                    className="param-select"
                    value={form.categorie}
                    onChange={handleFieldChange('categorie')}
                  >
                    {CATEGORIES.map((c) => (
                      <option key={c.value} value={c.value}>
                        {c.label}
                      </option>
                    ))}
                  </SelectRecherche>
                </label>
              </div>

              <div className="param-form-section">Durée</div>
              <div className="param-form-row">
                <label className="param-field">
                  <span className="param-label">Durée de référence (j)</span>
                  <input
                    type="number"
                    step="0.5"
                    min="0"
                    className="param-input"
                    value={form.dureeJours}
                    onChange={handleFieldChange('dureeJours')}
                    required
                  />
                </label>

                <label className="param-field">
                  <span className="param-label">Minimum (j)</span>
                  <input
                    type="number"
                    step="0.5"
                    min="0"
                    className="param-input"
                    value={form.dureeMinJours}
                    onChange={handleFieldChange('dureeMinJours')}
                  />
                </label>

                <label className="param-field">
                  <span className="param-label">Maximum (j)</span>
                  <input
                    type="number"
                    step="0.5"
                    min="0"
                    className="param-input"
                    value={form.dureeMaxJours}
                    onChange={handleFieldChange('dureeMaxJours')}
                  />
                </label>

                <label className="param-field">
                  <span className="param-label">Heures</span>
                  <input
                    type="number"
                    min="0"
                    className="param-input"
                    value={form.dureeHeures}
                    onChange={handleFieldChange('dureeHeures')}
                  />
                </label>
              </div>
              <p className="param-hint">
                Min et max : seulement pour une fourchette («&nbsp;2 à 3 journées&nbsp;»).
              </p>

              <div className="param-form-section">Tarif et public</div>
              <div className="param-form-row">
                <label className="param-field">
                  <span className="param-label">Tarif INTER (€ / pers / jour)</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    className="param-input"
                    value={form.prixJourInterHt}
                    onChange={handleFieldChange('prixJourInterHt')}
                    required
                  />
                </label>

                <label className="param-field">
                  <span className="param-label">Effectif minimum</span>
                  <input
                    type="number"
                    min="0"
                    className="param-input"
                    value={form.effectifMin}
                    onChange={handleFieldChange('effectifMin')}
                  />
                </label>

                <label className="param-field">
                  <span className="param-label">Effectif maximum</span>
                  <input
                    type="number"
                    min="0"
                    className="param-input"
                    value={form.effectifMax}
                    onChange={handleFieldChange('effectifMax')}
                  />
                </label>

                <label className="param-field">
                  <span className="param-label">Financement</span>
                  <SelectRecherche
                    className="param-select"
                    value={form.financement}
                    onChange={handleFieldChange('financement')}
                  >
                    {FINANCEMENTS.map((f) => (
                      <option key={f.value} value={f.value}>
                        {f.label}
                      </option>
                    ))}
                  </SelectRecherche>
                </label>
              </div>
              <p className="param-hint">
                Tarif INTRA&nbsp;:{' '}
                {form.prixJourInterHt
                  ? `${montant(Number(form.prixJourInterHt) * COEFFICIENT_INTRA)} € / jour de groupe`
                  : `${COEFFICIENT_INTRA} × le tarif INTER, en forfait de groupe`}
                , repris dans les devis.
              </p>

              <div className="param-form-section">Contenu</div>
              <div className="param-form-row">
                <label className="param-field param-field-grow">
                  <span className="param-label">Base légale</span>
                  <input
                    type="text"
                    className="param-input"
                    placeholder="Art. L.2315-18"
                    value={form.baseLegale}
                    onChange={handleFieldChange('baseLegale')}
                  />
                </label>

                <label className="param-field param-field-grow">
                  <span className="param-label">Lieu par défaut</span>
                  <input
                    type="text"
                    className="param-input"
                    value={form.lieuParDefaut}
                    onChange={handleFieldChange('lieuParDefaut')}
                  />
                </label>
              </div>

              <label className="param-field">
                <span className="param-label">Description</span>
                <textarea
                  className="param-textarea"
                  rows={3}
                  value={form.description}
                  onChange={handleFieldChange('description')}
                />
              </label>

              <label className="param-field">
                <span className="param-label">Objectif pédagogique</span>
                <textarea
                  className="param-textarea"
                  rows={2}
                  placeholder="Repris tel quel sur les lignes du devis."
                  value={form.objectifPedagogique}
                  onChange={handleFieldChange('objectifPedagogique')}
                />
              </label>

              <label className="param-checkbox-field">
                <input
                  type="checkbox"
                  checked={form.obligatoire}
                  onChange={handleFieldChange('obligatoire')}
                />
                <span>Formation légalement obligatoire</span>
              </label>

              <label className="param-checkbox-field">
                <input
                  type="checkbox"
                  checked={form.distancielPossible}
                  onChange={handleFieldChange('distancielPossible')}
                />
                <span>Distanciel possible (crée le tarif INTER distanciel)</span>
              </label>

              <label className="param-checkbox-field">
                <input type="checkbox" checked={form.actif} onChange={handleFieldChange('actif')} />
                <span>Formation active (proposée par l&apos;IA)</span>
              </label>

              <BarreProgression valeur={progressionEnregistrement} libelle="Enregistrement de la formation" estimation />
              <div className="param-form-actions">
                <button type="button" className="param-btn" onClick={fermerModale} disabled={saving}>
                  Annuler
                </button>
                <button type="submit" className="param-btn param-btn-primary" disabled={saving}>
                  {saving ? 'Enregistrement' : edition.id === null ? 'Créer' : 'Enregistrer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {suppression && (
        <div className="param-modal-overlay" onClick={() => setSuppression(null)}>
          <div className="param-modal param-modal-sm" onClick={(e) => e.stopPropagation()}>
            <div className="param-modal-header">
              <h2 className="param-modal-title">Supprimer la formation</h2>
            </div>
            <div className="param-modal-body">
              <p className="param-confirm-text">
                Voulez-vous vraiment supprimer <strong>{suppression.code}</strong> —{' '}
                {suppression.intitule} ? Elle est mise à la corbeille (restaurable pendant 30 jours) : ses
                tarifs et ses sessions au calendrier partent avec elle. Les devis déjà émis gardent
                leurs lignes.
              </p>
              <div className="param-form-actions">
                <button
                  type="button"
                  className="param-btn"
                  onClick={() => setSuppression(null)}
                  disabled={saving}
                >
                  Annuler
                </button>
                <button
                  type="button"
                  className="param-btn param-btn-danger-solid"
                  onClick={handleDelete}
                  disabled={saving}
                >
                  {saving ? 'Suppression' : 'Supprimer'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {suppressionGroupee && (
        <ConfirmationSuppression
          titre={
            suppressionGroupee === 'tout'
              ? 'Supprimer tout le catalogue ?'
              : `Supprimer ${selection.size} formation${selection.size > 1 ? 's' : ''} ?`
          }
          sousTitre={
            suppressionGroupee === 'tout'
              ? `${formations.length} formation${formations.length > 1 ? 's' : ''}`
              : [...selection]
                  .map((id) => formations.find((f) => f.id === id)?.code)
                  .filter(Boolean)
                  .join(', ')
          }
          details={['Les fiches et leurs tarifs.', 'Leurs sessions au calendrier.']}
          note="Les devis déjà émis gardent leurs lignes telles quelles."
          enCours={saving}
          onAnnuler={() => setSuppressionGroupee(null)}
          onConfirmer={handleSupprimerGroupe}
        />
      )}

      {importOuvert && (
        <ImportTexteModal
          titre="Importer des formations depuis un texte"
          aide="Collez le texte d’un catalogue. Une formation déjà connue (code ou intitulé) est mise à jour, pas dupliquée."
          exemple={
            'Exemple :\nTrésorerie du CSE — 1 jour — dès 300 €/j — budget du CSE\nBudgets AEP et ASC, obligations comptables, URSSAF, rapport de gestion…\n…'
          }
          motPluriel="formations"
          cleObjet="formation"
          colonnes={[
            { cle: 'code', resume: true, label: 'Code', obligatoire: true, largeur: 120, zone: 'titre' },
            { cle: 'intitule', resume: true, label: 'Intitulé', obligatoire: true, largeur: 220, zone: 'titre', grand: true },
            {
              cle: 'categorie',
              resume: true,
              label: 'Catégorie',
              type: 'select',
              options: CATEGORIES,
              largeur: 150,
            },
            { cle: 'dureeJours', resume: true, label: 'Jours', type: 'number', pas: '0.5', min: 0.5, obligatoire: true, largeur: 70 },
            { cle: 'prixJourInterHt', resume: true, label: '€ HT/j INTER', type: 'number', pas: '1', min: 0, obligatoire: true, largeur: 100 },
            { cle: 'effectifMin', resume: true, label: 'Eff. min', type: 'number', pas: '1', min: 0, largeur: 70 },
            { cle: 'effectifMax', resume: true, label: 'Eff. max', type: 'number', pas: '1', min: 0, largeur: 70 },
            { cle: 'obligatoire', label: 'Oblig.', type: 'checkbox' },
            { cle: 'distancielPossible', label: 'Distanciel', type: 'checkbox' },
            {
              cle: 'financement',
              label: 'Financement',
              type: 'select',
              options: FINANCEMENTS,
              largeur: 150,
            },
            { cle: 'description', label: 'Description', type: 'textarea', largeur: 240 },
          ]}
          chargerTexte={{ libelle: 'Récupérer le catalogue sur echo-cse.fr', charger: texteCatalogueSite }}
          analyserSource={() => analyserImportFormationsSite().then((res) => res.data)}
          demarrerDepuisSource={importOuvert === 'site'}
          remplacement={{
            libelle: `Supprimer d’abord les ${formations.length} formation${formations.length > 1 ? 's' : ''} du catalogue`,
            note: 'Leurs sessions au calendrier partent avec elles ; les devis émis gardent leurs lignes.',
            supprimerTout: () => supprimerToutesFormations().then((res) => res.data),
            libelleBilan: (b) => `${b.supprimees} supprimée${b.supprimees > 1 ? 's' : ''}${b.sessionsSupprimees ? `, ${b.sessionsSupprimees} session${b.sessionsSupprimees > 1 ? 's' : ''} du calendrier avec elles` : ''}`,
          }}
          analyser={(texte) => analyserImportFormations(texte).then((res) => res.data)}
          enregistrer={(retenues) =>
            enregistrerImportFormations(
              retenues.map((p) => ({ existantId: p.existantId, formation: p.formation }))
            ).then((res) => res.data)
          }
          onFermer={() => setImportOuvert(false)}
          onEnregistre={(bilan) => {
            if (bilan.creees > 0 || bilan.misesAJour > 0) {
              setFlash(
                `${bilan.creees} formation${bilan.creees > 1 ? 's' : ''} créée${bilan.creees > 1 ? 's' : ''}, ${bilan.misesAJour} mise${bilan.misesAJour > 1 ? 's' : ''} à jour depuis le texte.`
              );
              chargerFormations();
            }
          }}
        />
      )}
    </div>
  );
}

export default ParametrageFormations;
